import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Cms, ICarDetails, LoanResponse } from '@impact/data';
import { OverlayService } from '@impactdk/ngx-overlay';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

import { CheckoutService, UsedCarCheckoutFinancingTabsOrder } from '../../checkout/checkout.service';
import { objectIsEmpty } from '../../core/helpers';
import { SettingsService } from '../../core/settings.service';
import { ILeasingConfiguratorRanges } from '../../leasing/configurator/leasing-configurator.component';
import {
    ILeasingConfiguratorData,
    LEASING_PURPOSE_TYPES,
    LeasingService,
} from '../../leasing/leasing.service';
import { LoanService } from '../../loan/loan.service';
import { CheckoutFlowComponent } from '../../pages/checkout/checkout-flow.component';
import { UsedCarFinancingTypes, UsedCarPageSections } from '../used-car.models';
import { UsedCarService } from '../used-car.service';
import { cartIcon, checkmarkIcon, chevronRight, helpCircleIcon, steeringWheel } from '../../../icons';
import { IRadioGroupItem } from '../../form-elements/radio-toggle/radio-toggle.component';
import { SidePanelService } from '../../side-panel/side-panel.service';
import { IExpandedInsuranceCompany } from '../used-car-details.component';

@Component({
    selector: 'app-used-car-financing-sidebar',
    template: `
        <div class="used-car-financing-options" *ngIf="carDetails && pageData && isReady">
            <div class="used-car-financing__options">
                <!-- CASH -->
                <div class="used-car-financing__option" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Cash}">
                    <button class="used-car-financing__option-button"
                        *ngIf="carDetails?.price?.value"
                        (click)="setCheckoutMethod(usedCarFinancingTypes.Cash)">
                        <span class="primary">{{ 'used_car_details.financing_cash_price_text' | translate }}</span>
                        <span>
                            <span class="secondary">{{ carDetails?.price?.value }}</span>
                            <span class="tertiary">{{ 'generic.vat_included_text' | translate }}</span>
                        </span>
                        <span class="used-car-financing__option-active">${checkmarkIcon}</span>
                    </button>
                    <div class="used-car-financing__options-inline-content">
                        <ng-container *ngTemplateOutlet="CashFinance"></ng-container>
                    </div>
                </div>
                <!-- LOAN -->
                <div class="used-car-financing__option" *ngIf="loanData" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Loan}">
                    <button class="used-car-financing__option-button"
                        *ngIf="carDetails?.price?.value"
                        (click)="setCheckoutMethod(usedCarFinancingTypes.Loan)">
                        <span class="primary">{{ 'used_car_details.financing_loan_price_text' | translate }}</span>
                        <span>
                            <span class="secondary">
                                <app-dynamic-value [isLoading]="loanDataIsLoading$ | async">
                                    {{ loanData?.paymentPerMonth | defaultcurrency }}/{{ 'generic.month_abbreviation' | translate }}
                                </app-dynamic-value>
                            </span>
                            <span class="tertiary">{{ 'loan.down_payment' | translate }} {{ loanData?.downPayment | defaultcurrency }}</span>
                        </span>
                        <span class="used-car-financing__option-active">${checkmarkIcon}</span>
                    </button>
                    <div class="used-car-financing__options-inline-content">
                        <ng-container *ngTemplateOutlet="LoanFinance"></ng-container>
                    </div>
                </div>
                <!-- LEASING -->
                <div class="used-car-financing__option" *ngIf="allowLeasing && leasingConfigDataReady && leasingPriceAvailable" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Leasing}">
                    <button class="used-car-financing__option-button"
                        (click)="setCheckoutMethod(usedCarFinancingTypes.Leasing)">
                        <span class="primary">{{ 'used_car_details.financing_leasing_text' | translate }}</span>
                        <span>
                            <span class="secondary">
                                <app-dynamic-value [isLoading]="leasingDataIsLoading$ | async">
                                    {{ leasingConfiguratorData.monthlyPayment | number: '1.0-0' }}
                                    {{ leasingDataTexts?.general?.costSuffixText }}
                                </app-dynamic-value>
                            </span>
                            <span class="tertiary" *ngIf="leasingConfiguratorData?.downPayment">{{ 'loan.down_payment' | translate }} {{ leasingConfiguratorData?.downPayment | defaultcurrency }}</span>
                        </span>
                        <span class="used-car-financing__option-active">${checkmarkIcon}</span>
                    </button>
                    <div class="used-car-financing__options-inline-content">
                        <ng-container *ngTemplateOutlet="LeasingFinance"></ng-container>
                    </div>
                </div>
            </div>

            <div class="used-car-financing__options-content">
                <ng-container *ngTemplateOutlet="CashFinance"></ng-container>
                <ng-container *ngTemplateOutlet="LoanFinance"></ng-container>
                <ng-container *ngTemplateOutlet="LeasingFinance"></ng-container>
            </div>

            <div class="used-car-financing__bottom">
                <ng-container [ngSwitch]="isBuyable">
                    <div class="used-car-financing__bottom-actions double-pill-button__wrapper" *ngSwitchCase="true">
                        <button type="button" class="double-pill-button" (click)="openTestDriveSidePanel()">
                            <span class="double-pill-button__icon" aria-hidden="true">${steeringWheel}</span>
                            {{ 'used_car_details.book_test_drive' | translate }}
                        </button>
                        <button type="button" class="double-pill-button" (click)="goToCheckout(loanData)">
                            <span class="double-pill-button__icon">${cartIcon}</span>
                            {{ 'used_car_details.start_buying_process' | translate }}
                        </button>
                    </div>
                    <div class="used-car-financing__bottom-actions double-pill-button__wrapper--single" *ngSwitchCase="false">
                        <button type="button" class="double-pill-button" (click)="openTestDriveSidePanel()">
                            <span class="double-pill-button__icon" aria-hidden="true">${steeringWheel}</span>
                            {{ 'used_car_details.book_test_drive' | translate }}
                        </button>
                    </div>
                </ng-container>
                <div class="used-car-financing__bottom-usps">
                    <div class="used-car-financing__bottom-usp-item" *ngIf="carDetails?.dealer">
                        ${checkmarkIcon}
                        {{ 'used_car_details.financing_usp_vehicle_location_text' | translate }} {{carDetails?.dealer?.value}}
                    </div>
                    <div class="used-car-financing__bottom-usp-item" *ngIf="isBuyable && (onlineDepositAmount$ | async) as deposit">
                        ${checkmarkIcon}
                        {{ 'used_car_details.financing_usp_online_deposit_text' | translate }} {{ deposit | defaultcurrency }}
                    </div>
                </div>
            </div>
        </div>

        <!--
            TEMPLATES
        -->
        <ng-template #CashFinance>
            <div class="used-car-financing__options-content-item" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Cash}">
                <div class="used-car-financing__options-content-item-primary">
                    <div class="used-car-financing__options-content-item-top">
                        <h6 class="used-car-financing__options-content-header">{{ 'used_car_details.cash_price' | translate }}</h6>
                        <a *ngIf="pageData?.cashOptionInfoLink?.url" [href]="pageData?.cashOptionInfoLink?.url" class="used-car-financing__options-content-info-link" appInterceptLinks>
                            {{ 'used_car_details.financing_info_link_text' | translate }}
                            ${helpCircleIcon}
                        </a>
                    </div>
                    <div class="used-car-financing__options-content-line">
                        <div>{{ 'used_car_details.financing_cash_vehicle_price_text' | translate }}</div>
                        <div>{{ carDetails.price?.data | defaultcurrency }}</div>
                    </div>
                    <div class="used-car-financing__options-content-line" *ngIf="(onlineDepositAmount$ | async) as deposit">
                        <div>{{ 'used_car_details.financing_cash_vehicle_deposit_text' | translate }}</div>
                        <div>{{ deposit | defaultcurrency }}</div>
                    </div>

                    <div class="used-car-financing__options-content-info">
                        <div class="used-car-financing__options-content-info__header">{{ 'used_car_details.financing_cash_disclaimer_text_header' | translate }}</div>
                        <div>{{ 'used_car_details.financing_cash_disclaimer_text' | translate }}</div>
                    </div>
                </div>
                <ng-container *ngTemplateOutlet="optionalServices"></ng-container>
            </div>
        </ng-template>
        <ng-template #LoanFinance>
            <div class="used-car-financing__options-content-item" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Loan}">
                <div class="used-car-financing__options-content-item-primary">
                    <div class="used-car-financing__options-content-item-top">
                        <h6 class="used-car-financing__options-content-header">{{ 'used_car_details.financing_loan_price_text' | translate }}</h6>
                        <a *ngIf="pageData?.loanOptionInfoLink?.url" [href]="pageData?.loanOptionInfoLink?.url" class="used-car-financing__options-content-info-link" appInterceptLinks>
                            {{ 'used_car_details.financing_info_link_text' | translate }}
                            ${helpCircleIcon}
                        </a>
                    </div>
                    <app-loan
                        [loanAmount]="loanAmount"
                        [showHeader]="false"
                        [registrationDate]="registrationDate"
                        [altTheme]="true">
                    </app-loan>
                    <div class="used-car-financing__options-content-line">
                        <div>{{ 'loan.payment_per_month' | translate }}</div>
                        <div>
                            <app-dynamic-value [isLoading]="loanDataIsLoading$ | async">
                                {{ loanData?.paymentPerMonth | defaultcurrency }}/{{ 'generic.month_abbreviation' | translate }}
                            </app-dynamic-value>
                        </div>
                    </div>
                    <div class="used-car-financing__options-content-line">
                        <button type="button" class="button--as-link used-car-financing__options-link" (click)="showLoanSimulator(loanAmount)">
                            {{ 'loan.show_loan_terms_link_text' | translate }}
                            ${chevronRight}
                        </button>
                    </div>
                </div>
                <ng-container *ngTemplateOutlet="optionalServices"></ng-container>
            </div>
        </ng-template>
        <ng-template #LeasingFinance>
            <div class="used-car-financing__options-content-item" [ngClass]="{'active-option': selectedCheckoutMethod === usedCarFinancingTypes.Leasing}">
                <div class="used-car-financing__options-content-item-primary">
                    <div class="used-car-financing__options-content-item-top">
                        <h6 class="used-car-financing__options-content-header">{{ 'used_car_details.financing_leasing_text' | translate }}</h6>
                        <a *ngIf="pageData?.leasingOptionInfoLink?.url" [href]="pageData?.leasingOptionInfoLink?.url" class="used-car-financing__options-content-info-link" appInterceptLinks>
                            {{ 'used_car_details.financing_info_link_text' | translate }}
                            ${helpCircleIcon}
                        </a>
                    </div>
                    <app-leasing-configurator
                        *ngIf="leasingConfigDataReady"
                        [altTheme]="true"
                        [downPaymentRange]="leasingConfiguratorRangesData.downPaymentRange"
                        [leasingMonthsRange]="leasingConfiguratorRangesData.leasingMonthsRange"
                        [yearlyDistanceInKmRange]="leasingConfiguratorRangesData.yearlyDistanceInKmRange"

                        [downPayment]="leasingConfiguratorData.downPayment"
                        [monthlyTerms]="leasingConfiguratorData.monthlyTerms"
                        [yearlyDistanceInKm]="leasingConfiguratorData.yearlyDistanceInKm"

                        [showResidualValue]="false"
                        [leasingTexts]="leasingDataTexts"

                        [leasingTypes]="leasingTypes$ | async"
                        [leasingPurposes]="leasingPurposes$ | async"

                        (handleChange)="updateConfiguratorInputData($event)">
                    </app-leasing-configurator>
                    <div class="used-car-financing__options-content-line">
                        <div>{{ 'loan.payment_per_month' | translate }}</div>
                        <div>
                            <app-dynamic-value [isLoading]="leasingDataIsLoading$ | async">
                                {{ leasingConfiguratorData?.monthlyPayment | number: '1.0-0' }}
                                {{ leasingDataTexts?.general?.costSuffixText }}
                            </app-dynamic-value>
                        </div>
                    </div>
                    <div class="used-car-financing__options-content-line" *ngIf="pageData?.leasingTermsLink?.url">
                        <a [href]="pageData?.leasingTermsLink?.url"
                            class="button--as-link used-car-financing__options-link"
                            appInterceptLinks>
                            {{ 'used_car_details.financing_leasing_terms_link_text' | translate }}
                            ${chevronRight}
                        </a>
                    </div>
                    <div class="used-car-financing__options-content-line secondary-text">
                        <div>
                            {{ 'used_car_details.financing_leasing_total_period_text' | translate }}
                            {{ totalLeasingCost | defaultcurrency }}
                        </div>
                    </div>
                </div>
                <ng-container *ngIf="showServices || insuranceCompany">
                    <ng-container *ngTemplateOutlet="optionalServices"></ng-container>
                </ng-container>
            </div>
        </ng-template>

        <ng-template #optionalServices>
            <div class="used-car-financing__optional-services">
                <div class="used-car-financing__optional-services-item" *ngIf="selectedCheckoutMethod === usedCarFinancingTypes.Leasing && showServices">
                    <button type="button"
                        class="optional-services-button"
                        [ngClass]="{'selected-option': true}">
                        <span class="optional-services-button__checkmark">
                            ${checkmarkIcon}
                        </span>
                        <span class="optional-services-button__text">
                            <span>{{ 'used_car_details.financing_leasing_text' | translate }}</span>
                            <span>{{ 'used_car_details.financing_leasing_option_secondary_text' | translate }}</span>
                        </span>
                    </button>
                </div>
                <div class="used-car-financing__optional-services-item" *ngIf="selectedCheckoutMethod !== usedCarFinancingTypes.Leasing && showServices">
                    <button type="button"
                        class="optional-services-button"
                        [ngClass]="{'selected-option': true}">
                        <span class="optional-services-button__checkmark">
                            ${checkmarkIcon}
                        </span>
                        <span class="optional-services-button__text">
                            <span>{{ 'used_car_details.financing_option_select_text' | translate }}</span>
                            {{ 'used_car_details.financing_service_subscription_header_text' | translate }}
                        </span>
                    </button>
                    <button class="optional-services__scroll-to-section-button" (click)="onOptionsSettingsButtonClick(usedCarPageSections.SERVICE)">
                        {{ 'used_car_details.financing_option_adjust_text' | translate }} ${chevronRight}
                    </button>
                </div>
                <div class="used-car-financing__optional-services-item" *ngIf="insuranceCompany">
                    <button type="button"
                        class="optional-services-button"
                        [ngClass]="{'selected-option': insuranceCompany?.isSelected}"
                        (click)="onInsuranceToggleButtonClick(insuranceCompany)">
                        <span class="optional-services-button__checkmark">
                            ${checkmarkIcon}
                        </span>
                        <span class="optional-services-button__text">
                            <span>{{ 'used_car_details.financing_option_select_text' | translate }}</span>
                            {{insuranceCompany?.companyName}} {{ insuranceCompany?.pricePeriodText }}
                        </span>
                        <span class="optional-services-button__price">{{ insuranceCompany?.priceText }}</span>
                    </button>
                    <button class="optional-services__scroll-to-section-button" (click)="onOptionsSettingsButtonClick(usedCarPageSections.INSURANCE)">
                        {{ 'used_car_details.financing_option_adjust_text' | translate }} ${chevronRight}
                    </button>
                </div>
            </div>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsedCarFinancingSidebarComponent implements AfterViewInit, OnDestroy, OnChanges {
    @Input() loanAmount?: number;
    @Input() registrationDate?: string | null | undefined;
    @Input() leasingDataTexts: any;
    @Input() insuranceCompany: IExpandedInsuranceCompany | null;
    @Input() allowLeasing: boolean;
    @Input() isBuyable: boolean | undefined;
    @Input() showServices: boolean;
    @Output() scrollToSection = new EventEmitter();
    @Output() insuranceToggleClick = new EventEmitter();

    usedCarFinancingTabsOrder = UsedCarCheckoutFinancingTabsOrder;
    usedCarFinancingTypes = UsedCarFinancingTypes;
    usedCarPageSections = UsedCarPageSections;

    pageData$: Observable<Cms.UsedCarDetailsPage | undefined>;
    disclaimerText$: Observable<string>;
    car$: Observable<ICarDetails>;
    loanDataIsLoading$: Observable<boolean>;
    leasingDataIsLoading$: Observable<boolean>;
    banks$: Observable<any[]>;
    defaultBank$: Observable<any | undefined>;
    leasingServiceTypes = LEASING_PURPOSE_TYPES;

    loanData: LoanResponse;
    onlineDepositAmount$: Observable<number | undefined>;

    leasingTotalCost: number;
    leasingDataLoading = false;
    selectedCheckoutMethod: UsedCarFinancingTypes;
    leasingPriceIsValid: boolean;
    totalLeasingCost: number;

    leasingTypes$: Observable<IRadioGroupItem[]>;
    leasingPurposes$: Observable<IRadioGroupItem[]>;

    carDetails: ICarDetails;
    pageData: Cms.UsedCarDetailsPage | undefined;

    private unsubscribe = new Subject<void>();

    leasingConfiguratorData: ILeasingConfiguratorData;
    leasingConfiguratorRangesData: ILeasingConfiguratorRanges;

    // UI variables
    leasingConfigDataLoading: boolean;
    leasingConfigDataReady: boolean;
    isReady: boolean;

    constructor(
        private loanService: LoanService,
        private cdr: ChangeDetectorRef,
        private checkoutService: CheckoutService,
        private leasingService: LeasingService,
        private overlayService: OverlayService,
        private usedCarService: UsedCarService,
        private sidePanelService: SidePanelService,
        settingsService: SettingsService
    ) {
        this.leasingConfigDataLoading = true;
        this.leasingConfigDataReady = false;
        this.isReady = false;
        this.loanDataIsLoading$ = this.loanService.isLoading$;
        this.leasingDataIsLoading$ = this.leasingService.leasingPriceLoading$;
        this.car$ = this.usedCarService.carDetails$;
        this.banks$ = this.usedCarService.getBanks();

        const settings$ = settingsService.get().pipe(
            shareReplay<Cms.SettingsDto>({ bufferSize: 1, refCount: true })
        );

        this.defaultBank$ = settings$.pipe(
            map(settings => settings.loanCalculator.defaultBank)
        );

        this.leasingTypes$ = settings$.pipe(
            map((settings: Cms.SettingsDto) =>
                settings.leasingCalculator.leasingTypes.map((type: Cms.LeasingType): IRadioGroupItem => ({
                    label: type.label,
                    value: type.key
                }))
            )
        )

        this.leasingPurposes$ = settings$.pipe(
            map((settings: Cms.SettingsDto) =>
                settings.leasingCalculator.leasingPurposes.map((type: Cms.LeasingPurpose): IRadioGroupItem => ({
                    label: type.label,
                    value: type.key
                }))
            )
        )

        this.onlineDepositAmount$ = settings$.pipe(
            map(settings => settings.checkout.checkoutDepositAmount)
        );

        this.loanService.loanData.subscribe((data: any) => {
            this.loanData = data;
            this.cdr.markForCheck();
        });

        const carDetailsData$ = this.usedCarService.carDetails$;
        const pageData$ = this.usedCarService.pageData$;
        const leasingConfigDataAndRanges$ = this.leasingService.leasingConfigDataAndRanges$;
        const connectStream$ = combineLatest([carDetailsData$, pageData$, leasingConfigDataAndRanges$]);

        connectStream$.pipe(
            map(([carDetailsData, pageData, leasingConfigDataAndRanges]) => {
                this.carDetails = carDetailsData;
                this.pageData = pageData;
                this.leasingConfiguratorData = leasingConfigDataAndRanges.leasingData;
                this.leasingConfiguratorRangesData = leasingConfigDataAndRanges.leasingRanges;

                if (!objectIsEmpty(leasingConfigDataAndRanges)) {
                    this.getUsedCarPricePerMonth().pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
                        this.totalLeasingCost = (data?.pricePerMonth * Number(this.leasingConfiguratorData.monthlyTerms)) + Number(this.leasingConfiguratorData.downPayment);
                        this.leasingConfigDataLoading = false;
                        this.leasingConfigDataReady = true;
                    });
                }

                this.isReady = true;
                this.cdr.markForCheck();
            }), takeUntil(this.unsubscribe)).subscribe();
    }

    ngAfterViewInit(): void {
        /**
         * Sets the checkout method based on the selected financing type.
         * Subscribes to the financing type observable and updates the checkout method accordingly.
         *
         * @return {void}
         */
        this.usedCarService.financingType$.pipe(takeUntil(this.unsubscribe)).subscribe((type) => {
            switch (type) {
                case UsedCarFinancingTypes.Cash:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Cash);
                    break;
                case UsedCarFinancingTypes.Loan:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Loan);
                    break;
                case UsedCarFinancingTypes.Leasing:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Leasing);
                    break;
                default:
                    this.setCheckoutMethod(UsedCarFinancingTypes.Cash);
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.insuranceCompany) {
            this.insuranceCompany = changes.insuranceCompany.currentValue;
            this.cdr.markForCheck();
        }
    }

    getUsedCarPricePerMonth(): Observable<any> {
        return this.leasingService.getUsedCarLeasingPrice(this.carDetails, this.leasingConfiguratorData);
    }

    get leasingPriceAvailable(): boolean {
        return !!this.carDetails?.hasLeasingPricePrivateOperational?.data;
      }

    /**
     * Updates the configurator input data with the provided values and retrieves the leasing price.
     *
     * @param {any} data - The input data containing down payment, monthly terms, yearly distance in km, residual value, leasing plan type, and leasing purpose.
     * @return {void}
     */
    updateConfiguratorInputData(data: any) {
        this.leasingConfigDataLoading = true;
        this.cdr.detectChanges();

        if (isNaN(data.residualValue)) {
            data.residualValue = 0;
        }

        this.leasingConfiguratorData.downPayment = data.downPayment;
        this.leasingConfiguratorData.monthlyTerms = data.monthlyTerms;
        this.leasingConfiguratorData.yearlyDistanceInKm = data.yearlyDistanceInKm;
        this.leasingConfiguratorData.residualValue = data.residualValue;
        this.leasingConfiguratorData.leasingType = data.leasingPlanType;
        this.leasingConfiguratorData.leasingtypeForPriceCalculation = data.leasingPurpose;

        // Get leasing price
        this.getUsedCarPricePerMonth().pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
            this.leasingPriceIsValid = data.pricePerMonth > 1000;
            this.leasingConfiguratorData.monthlyPayment = data?.pricePerMonth;
            this.totalLeasingCost = (data?.pricePerMonth * Number(this.leasingConfiguratorData.monthlyTerms)) + Number(this.leasingConfiguratorData.downPayment);
            this.leasingConfigDataLoading = false;
            this.leasingConfigDataReady = true;
            this.cdr.markForCheck();
        });
    }

    /**
     * Sets the checkout method for the used car financing process.
     *
     * @param {UsedCarFinancingTypes} value - The selected checkout method.
     * @return {void}
     */
    setCheckoutMethod(value: UsedCarFinancingTypes) {
        this.selectedCheckoutMethod = value;
        this.cdr.markForCheck();
    }

    /**
     * Initiates the checkout process for a used car, either through leasing or a loan.
     *
     * @param {LoanResponse} [loanData] - Optional loan data to be used for checkout.
     * @return {void}
     */
    goToCheckout(loanData?: LoanResponse) {
        // Update leasing data in checkout service
        if (this.selectedCheckoutMethod === UsedCarFinancingTypes.Leasing) {
            this.checkoutService.setLeasingData(this.leasingConfiguratorData, this.leasingConfiguratorRangesData);
        }

        this.car$.pipe(takeUntil(this.unsubscribe)).subscribe((car) => {
            const checkoutInitData = {
                vin: car?.vin?.value,
                carId: car?.id,
                loanData,
                leasingData: this.leasingConfiguratorData,
            };

            this.checkoutService.initCheckout(checkoutInitData, undefined, loanData ? 'loan' : 'cash');

            this.overlayService.open(CheckoutFlowComponent, {
                panelClass: 'full-page-takeover',
                fullHeight: true,
                data: {
                    checkoutInitData,
                    selectedCheckoutMethod: this.selectedCheckoutMethod,
                    allowLeasing: !!this.allowLeasing
                },
            });
        });
    }

    /**
     * Opens the test drive side panel for a used car.
     *
     * @return {void}
     */
    openTestDriveSidePanel() {
        this.sidePanelService.showBookUsedCarTestDriveSidePanel(this.car$);
    }

    /**
     * Opens the loan simulator side panel to calculate loan details.
     *
     * @param {number} price - Optional price of the object to be used for loan calculation.
     * @return {void}
     */
    showLoanSimulator(price?: number): void {
        if (price) {
            this.sidePanelService.showLoanCalculatorSidePanel({
                objectPrice: price,
                registrationDate: this.carDetails?.registrationDate?.data ?? undefined,
            });
        }
    }

    /**
     * Handles the click event on options button.
     *
     * @param {UsedCarPageSections} section - The section to scroll to.
     * @return {void}
     */
    onOptionsSettingsButtonClick(section: UsedCarPageSections): void {
        this.scrollToSection.emit(section);
    }

    onInsuranceToggleButtonClick(insurance: IExpandedInsuranceCompany): void {
        this.insuranceToggleClick.emit(insurance);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
